import React from 'react';
import { Settings, CreditCard, Droplet } from 'lucide-react';

/**
 * Секция формы с дополнительными параметрами
 */
const AdditionalParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  glueTypes
}) => {
  return (
    <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Settings className="w-5 h-5 mr-2 text-amber-500" />
        Дополнительные параметры
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Запас материала */}
        <div>
          <label htmlFor="wastagePercent" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Запас материала (%)
          </label>
          <input
            type="text"
            id="wastagePercent"
            name="wastagePercent"
            value={formData.wastagePercent}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.wastagePercent ? 'input-error' : ''}`}
            placeholder="Процент запаса"
          />
          {errors.wastagePercent && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.wastagePercent}
            </p>
          )}
        </div>

        {/* Тип клея */}
        <div>
          <label htmlFor="glueType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Droplet className="w-4 h-4 mr-2 text-amber-500" />
            Тип клея
          </label>
          <select
            id="glueType"
            name="glueType"
            value={formData.glueType}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {glueTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* Чекбокс для потолка */}
        <div className="col-span-full">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="includeСeiling"
              checked={formData.includeСeiling}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Включить оклейку потолка
            </span>
          </label>
        </div>

        {/* Чекбокс для грунтовки */}
        <div className="col-span-full">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="includePrimeCoat"
              checked={formData.includePrimeCoat}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Добавить грунтовку в расчет
            </span>
          </label>
        </div>
      </div>

      {/* Цены для экономического расчета */}
      <div className="mt-5">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 flex items-center">
          <CreditCard className="w-4 h-4 mr-2 text-amber-500" />
          Экономический расчет
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Цена за рулон обоев */}
          <div>
            <label htmlFor="pricePerRoll" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Цена за {formData.wallpaperType === 'liquid' ? 'упаковку' : 'рулон'} (руб.)
            </label>
            <input
              type="text"
              id="pricePerRoll"
              name="pricePerRoll"
              value={formData.pricePerRoll}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.pricePerRoll ? 'input-error' : ''}`}
              placeholder="Цена"
            />
            {errors.pricePerRoll && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.pricePerRoll}
              </p>
            )}
          </div>

          {/* Цена за клей */}
          {formData.glueType !== 'none' && (
            <div>
              <label htmlFor="pricePerGlue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Цена за упаковку клея (руб.)
              </label>
              <input
                type="text"
                id="pricePerGlue"
                name="pricePerGlue"
                value={formData.pricePerGlue}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.pricePerGlue ? 'input-error' : ''}`}
                placeholder="Цена"
              />
              {errors.pricePerGlue && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.pricePerGlue}
                </p>
              )}
            </div>
          )}

          {/* Цена за грунтовку */}
          {formData.includePrimeCoat && (
            <div>
              <label htmlFor="pricePerPrimeCoat" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Цена за литр грунтовки (руб.)
              </label>
              <input
                type="text"
                id="pricePerPrimeCoat"
                name="pricePerPrimeCoat"
                value={formData.pricePerPrimeCoat}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.pricePerPrimeCoat ? 'input-error' : ''}`}
                placeholder="Цена"
              />
              {errors.pricePerPrimeCoat && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.pricePerPrimeCoat}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Подсказка по запасу материала */}
      <div className="mt-4 bg-white dark:bg-gray-800 rounded-md p-3 border border-amber-100 dark:border-amber-800 text-sm">
        <p className="text-gray-700 dark:text-gray-300">
          <span className="font-medium">Рекомендуемый запас:</span> 10-15% для обычных обоев, 15-20% для обоев с крупным рисунком.
          Запас необходим для компенсации ошибок при поклейке и возможных ремонтов в будущем.
        </p>
      </div>
    </div>
  );
};

export default AdditionalParametersSection;